import { useState } from 'react';
import { AdminPermissionsEditor, PERMISSION_PRESETS, materialize, summarize } from './AdminPermissions';
import { saveRole, deleteRole } from '../lib/adminRoles';

const field = 'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-300';

function RoleModal({ role, onClose, onSaved, showToast }) {
  const [name, setName] = useState(role?.name || '');
  const [desc, setDesc] = useState(role?.description || '');
  const [perms, setPerms] = useState(role ? materialize(role.permissions) : {});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (name.trim().length < 2) return showToast?.('Poné un nombre al perfil', 'error');
    setBusy(true);
    try { await saveRole({ id: role?.id, name, description: desc, permissions: materialize(perms) }); showToast?.('Perfil guardado'); await onSaved?.(); onClose(); }
    catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-gray-900">{role ? 'Editar perfil de admin' : 'Nuevo perfil de admin'}</h3>
          <button onClick={onClose} className="text-gray-400 text-xl leading-none">×</button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Nombre
            <input className={`${field} mt-1 font-normal normal-case`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Contadora, Encargado de sede" /></label>
          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Descripción (opcional)
            <input className={`${field} mt-1 font-normal normal-case`} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Para qué se usa este perfil" /></label>
        </div>
        {!role && (
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">Empezar desde una plantilla</p>
            <div className="flex flex-wrap gap-1.5">
              {PERMISSION_PRESETS.map((p) => (
                <button key={p.name} type="button" title={p.desc} onClick={() => { setPerms(p.perms); if (!name) setName(p.name); if (!desc) setDesc(p.desc); }}
                  className="text-xs font-bold px-3 py-1.5 rounded-xl bg-indigo-50 text-indigo-700 border border-indigo-100">{p.name}</button>
              ))}
            </div>
          </div>
        )}
        <AdminPermissionsEditor value={perms} onChange={setPerms} />
        <button disabled={busy} onClick={save} className="w-full py-3 rounded-2xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">Guardar perfil</button>
      </div>
    </div>
  );
}

// Perfiles de admin (Configuración): conjuntos de permisos con nombre, reutilizables
export default function AdminRoles({ roles, admins, onChanged, showToast }) {
  const [edit, setEdit] = useState(null); // {} = nuevo, role = editar
  const used = (id) => admins.filter((a) => a.admin_role_id === id).length;
  const remove = async (r) => {
    const n = used(r.id);
    if (!window.confirm(n ? `${n} administrador(es) usan este perfil. Si lo borrás, conservan los permisos que tienen ahora pero ya no se actualizan con el perfil.\n\n¿Borrar "${r.name}"?` : `¿Borrar el perfil "${r.name}"?`)) return;
    try { await deleteRole(r.id); showToast?.('Perfil borrado'); await onChanged?.(); } catch (e) { showToast?.(e.message, 'error'); }
  };
  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-900 text-sm">Perfiles de admin</h3>
          <p className="text-xs text-gray-400 mt-0.5">Conjuntos de permisos con nombre. Al asignar un perfil a un admin, si después cambiás el perfil se actualizan todos los que lo usan.</p>
        </div>
        <button onClick={() => setEdit({})} className="px-3 py-2 rounded-xl text-xs font-bold text-white flex-shrink-0" style={{ background: 'var(--brand-grad)' }}>+ Nuevo perfil</button>
      </div>
      {roles.length === 0 && <p className="text-sm text-gray-400 text-center py-6">Todavía no hay perfiles. Creá el primero desde una plantilla (Supervisor, Contador, Recursos Humanos…).</p>}
      <div className="space-y-2">
        {roles.map((r) => {
          const s = summarize(r.permissions);
          return (
            <div key={r.id} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3.5 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900">{r.name}</p>
                {r.description && <p className="text-xs text-gray-400 truncate">{r.description}</p>}
                <p className="text-[11px] text-sky-700 mt-1">{s.visible} secciones visibles · {s.editable} con edición · {used(r.id)} admin{used(r.id) === 1 ? '' : 's'}</p>
              </div>
              <button onClick={() => setEdit(r)} className="px-3 py-1.5 rounded-xl text-xs font-bold bg-white border border-gray-200 text-gray-600">Editar</button>
              <button onClick={() => remove(r)} className="w-8 h-8 rounded-xl bg-white border border-red-100 text-red-400" aria-label="Borrar">×</button>
            </div>
          );
        })}
      </div>
      {edit && <RoleModal role={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={onChanged} showToast={showToast} />}
    </div>
  );
}
